import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshNavLink as NavLink } from './RefreshLink';
import { IcCalculator, IcClipboardList, IcHistory, IcBanknote, IcSparkles, IcChevronDown } from '../theme/icons';

// Shared sub-navigation for the « Ventes » area. Order (per owner request):
// Nouvelle vente → Réservation → Historique (single tab covering both history
// pages, owner Studio 2026-08-13). Desktop: horizontal strip, horizontally
// scrollable. Mobile (owner Studio 2026-08-14): the three tabs collapse into a
// single dropdown button that keeps the exact place, size and design of the
// « Nouvelle vente » chip; the right-aligned children (Produits/Services
// toggle on the seller-sales page) are untouched. When a history page is
// active, a right-aligned segmented switch « Ventes | Services » (same design
// as Fournisseurs/Laboratoires) picks the history shown.
const TABS = [
  { to: '/seller-sales', label: 'Nouvelle vente', icon: IcCalculator },
  { to: '/reservations', label: 'Réservation', icon: IcClipboardList },
  { to: '/seller-history', label: 'Historique', icon: IcHistory, match: ['/seller-history', '/service-history'] },
];

const HISTORY_SWITCH = [
  { to: '/seller-history', label: 'Ventes', icon: IcBanknote },
  { to: '/service-history', label: 'Services', icon: IcSparkles },
];

const VenteTabs = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHistory = HISTORY_SWITCH.some((o) => pathname.startsWith(o.to));
  const active =
    TABS.find((t) => pathname === t.to || (t.match && t.match.some((m) => pathname.startsWith(m)))) || TABS[0];

  const rightSlot = (onHistory || children) && (
    <div className="ml-auto inline-flex items-center gap-2 shrink-0">
      {/* Kit « pilule » .segp — même bascule que Soin / Produits de /reservations
          (owner 2026-09-06). Les libellés restent Ventes / Services. */}
      {onHistory && (
        <div className="segp" aria-label="Basculer entre historique ventes et services">
          {HISTORY_SWITCH.map((o) => (
            <NavLink
              key={o.to}
              to={o.to}
              end
              className={({ isActive }) => (isActive ? 'segp-on' : 'segp-off')}
            >
              <o.icon size={13} /> {o.label}
            </NavLink>
          ))}
        </div>
      )}
      {children}
    </div>
  );

  return (
    <div className="mb-4">
      {/* Mobile : les trois onglets dans un seul bouton déroulant — même
          place, même taille, même design que le chip « Nouvelle vente »
          (chip actif : blanc, bordure noire). Children à droite, inchangés. */}
      <div className="md:hidden flex gap-2 items-center">
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-white border-gray-900 text-gray-900 shadow-sm"
          >
            <active.icon size={13} /> {active.label}
            <IcChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="absolute z-40 top-full mt-1 left-0 min-w-[13rem] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
              {TABS.map((t) => {
                const forced = t.match && t.match.some((m) => pathname.startsWith(m));
                return (
                  <NavLink
                    key={t.to}
                    to={t.to}
                    end
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-4 py-3 text-sm font-semibold border-b border-gray-50 last:border-b-0 ${
                        isActive || forced ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                      }`
                    }
                  >
                    <t.icon size={13} /> {t.label}
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
        {rightSlot}
      </div>

      {/* Desktop : strip horizontal inchangé. */}
      <div className="hidden md:block -mx-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex gap-2 px-1 min-w-full items-center">
          {TABS.map((t) => {
            const forced = t.match && t.match.some((m) => pathname.startsWith(m));
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end
                className={({ isActive }) =>
                  `shrink-0 ${isActive || forced ? 'tab-chip-on' : 'tab-chip'}`
                }
              >
                <t.icon size={13} /> {t.label}
              </NavLink>
            );
          })}
          {rightSlot}
        </div>
      </div>
    </div>
  );
};

export default VenteTabs;
